import type { ReactElement } from "react";
import { Link, useLocation } from "react-router";

import { useApiData } from "../../api/use-api-data.js";
import { usePageMeta } from "../../app/use-page-meta.js";
import { ApiState, titleFor } from "../../components/api-state.js";
import { useRowLink } from "../../components/row-link.js";

import { CenterBackLink } from "./center-back-link.js";
import { CsvDownload, csvLimitLabel } from "./csv-download.js";
import { MailSearchForm, type MailConditions } from "./mail-search-form.js";
import { adminCenterApi, adminCenterUrls } from "./urls.js";

/** 送ったメールの 1 行（サーバの CenterMailRow と同じ欄）。 */
export interface CenterMailRow {
    readonly sentMailId: number;
    readonly tenantId: number;
    readonly to: string;
    readonly subject: string;
    readonly sentAt: string;
    readonly hasTenant: boolean;
    readonly statusName: string;
    readonly statusClass: string;
    readonly deliveryName: string;
    readonly deliveryClass: string;
}

/** 送ったメールを探す画面（<c>GET /api/admin-center/mails</c>。サーバの CenterMailSearchViewModel）。 */
export interface CenterMailSearch extends MailConditions {
    readonly rows: readonly CenterMailRow[];
    readonly periodReversed: boolean;
    readonly filled: boolean;
    /** CSV に書き出す行の上限（サーバが決める。新しい順にここまで）。 */
    readonly csvLimit: number;
}

function MailRows({ rows }: { readonly rows: readonly CenterMailRow[] }): ReactElement {
    const rowLink = useRowLink();

    return (
        <tbody>
            {rows.map((row) => {
                const href = adminCenterUrls.mail(row.sentMailId);
                return (
                    // 行ごと押せる。**「本文を見る」は残す**——キーボードと読み上げはそちらを辿る
                    <tr key={row.sentMailId} data-row-href={href} onClick={rowLink(href)}>
                        <td className="whitespace-nowrap text-slate-500">{row.sentAt}</td>
                        {/* 宛先と件名には最小の幅を持たせる（PC の幅で 7 列が収まる値）。長いメールアドレスは途中で折り返す——折り返せないと宛先だけで幅を取り、件名が細く潰れる */}
                        <td className="min-w-48 break-all">{row.to}</td>
                        {/* 探しているのはたいてい件名。**ここだけ太くする** */}
                        <td className="min-w-60 font-medium text-ink-800">{row.subject}</td>
                        {/* 見た目の出し分け（失敗だけ目立たせる）はサーバが決める。**札は折り返さない**——列が狭いと 1 文字ずつ縦に並ぶ */}
                        <td className="whitespace-nowrap"><span className={row.statusClass}>{row.statusName}</span></td>
                        <td className="whitespace-nowrap"><span className={row.deliveryClass}>{row.deliveryName}</span></td>
                        <td className="cell-number">
                            {row.hasTenant
                                ? <Link to={adminCenterUrls.tenant(row.tenantId)} className="btn-secondary btn-sm">{row.tenantId}</Link>
                                : <span className="badge-muted">契約前</span>}
                        </td>
                        <td className="cell-actions">
                            <Link to={href} className="btn-secondary btn-sm">本文を見る</Link>
                        </td>
                    </tr>
                );
            })}
        </tbody>
    );
}

/**
 * 送ったメールの確認。
 * <b>送っていないのか、送ったが届いていないのか</b>を切り分けるための画面。条件は URL のクエリ。
 */
export function MailSearchPage(): ReactElement | null {
    const { search } = useLocation();
    const data = useApiData<CenterMailSearch>(adminCenterApi.mails(search));
    usePageMeta(titleFor(data, "送ったメールの確認"));

    if (data.state !== "ready") {
        return <ApiState data={data} what="送ったメール" />;
    }
    const found = data.value;

    return (
        <div className="page max-w-6xl">
            <CenterBackLink />
            <div className="page-header">
                <div>
                    <h1 className="page-title">送ったメールの確認</h1>
                    <p className="page-subtitle">
                        実際に送った記録を探します。
                        <strong className="text-ink-800">送っていないのか、送ったが届いていないのか</strong>を
                        切り分けられます。
                    </p>
                </div>
            </div>

            <MailSearchForm conditions={found} />

            {found.periodReversed
                ? (
                    <div className="alert-warning mb-4">
                        <strong>期間の指定が前後で逆になっています。</strong>
                        「この日から」が「この日まで」より後ろです。
                    </div>
                )
                : null}

            {found.rows.length === 0
                ? (
                    <div className="empty-state text-sm text-slate-500">
                        当たる記録がありませんでした。<strong className="text-ink-800">送っていない</strong>か、
                        条件が絞られすぎています。宛先の打ち間違いもご確認ください。
                    </div>
                )
                : (
                    <>
                        <CsvDownload
                            filePrefix="sent_mails"
                            url={adminCenterApi.mailsCsv({
                                keyword: found.keyword, tenantId: found.tenantId, fromDate: found.fromDate, toDate: found.toDate,
                                status: found.status, delivery: found.delivery,
                            })}
                            summary={`${String(found.rows.length)} 件${found.filled ? "（上限。期間で絞ってください）" : ""}`}
                            help={`CSV には、画面の上限に関係なく当たる記録を新しい順に ${csvLimitLabel(found.csvLimit)} 件まで書き出します（本文は含みません）。`}
                        />
                        <div className="table-card">
                            <div className="table-scroll">
                                <table className="data-table">
                                    <thead>
                                        <tr>
                                            <th scope="col">送信日時</th>
                                            <th scope="col">宛先</th>
                                            <th scope="col">件名</th>
                                            <th scope="col">送信</th>
                                            <th scope="col">配送</th>
                                            <th scope="col" className="cell-number">契約 ID</th>
                                            <th scope="col"></th>
                                        </tr>
                                    </thead>
                                    <MailRows rows={found.rows} />
                                </table>
                            </div>
                        </div>
                    </>
                )}
        </div>
    );
}
